import { Dispatch, SetStateAction, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserKeys, takeActionOnDevices } from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import { MobileDevice } from '../../../../../types';
import SharedDialog from '../../../../shared/shared-dialog';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  ToolsRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import { isFeatureEnabled } from '../../../../utils/feature-flags';
import { useUserSettings } from '../../../../utils/use-user-settings';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

interface BlockUserDeviceDialogProps {
  open: boolean;
  handleClose: () => void;
  userEmail?: string;
  selectedDevices: MobileDevice[];
  changeLogRoles: string[];
  setSelectedDeviceIds: Dispatch<SetStateAction<string[]>>;
}

export default function BlockUserDeviceDialog({
  open,
  handleClose,
  userEmail,
  selectedDevices,
  changeLogRoles,
  setSelectedDeviceIds,
}: BlockUserDeviceDialogProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const { features } = useUserSettings();

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      takeActionOnDevices('BLOCK', selectedDevices, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message:
          selectedDevices.length > 1
            ? 'Devices blocked successfully!'
            : 'Device blocked successfully!',
      });

      if (isFeatureEnabled('indexMobileDevicesReact', features)) {
        queryClient.invalidateQueries({
          queryKey: [UserKeys.MobileDevicesIndexedPage],
        });
      } else if (isFeatureEnabled('showMobileManagementFilters', features)) {
        queryClient.setQueryData(
          [UserKeys.SearchedMobileDevices],
          (oldData: MobileDevice[]) => {
            if (oldData) {
              const deviceIds = selectedDevices.map(
                (device) => device.resourceId
              );

              return oldData.map((device) => {
                if (deviceIds.includes(device.resourceId)) {
                  device.status = 'BLOCKED';
                }
                return device;
              });
            }
          }
        );
      } else {
        queryClient.setQueryData(
          [UserKeys.Devices, userEmail],
          (oldData: MobileDevice[]) => {
            if (oldData) {
              const deviceIds = selectedDevices.map(
                (device) => device.resourceId
              );

              return oldData.map((device) => {
                if (deviceIds.includes(device.resourceId)) {
                  device.status = 'BLOCKED';
                }
                return device;
              });
            }
          }
        );
      }

      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          selectedDevices.length > 1
            ? 'There was an issue blocking these devices. Please refresh and try again.'
            : 'There was an issue blocking this device. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(
        userEmail
          ? analytics.user_management.user_details.devices.block_mobile_device
          : analytics.devices.mobile_management.block_mobile_device,
        userEmail
          ? {
              module: ModuleList.modules.userManagement.name,
              section: ModuleList.modules.userManagement.sections.mobileDevices,
            }
          : {
              module: ModuleList.modules.devices.name,
              section: ModuleList.modules.devices.sections.mobileManagement,
            }
      );
      setSelectedDeviceIds([]);
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (isAbleToSeeUIElement(changeLogRoles, ToolsRoles.CanBlockMobile.value)) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  if (!selectedDevices?.length) return null;

  const isBulk = selectedDevices.length > 1;

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title={isBulk ? 'Block Devices' : 'Block Device'}
      dialogContent={
        <Box>
          <Typography>
            {isBulk ? (
              <>
                Block <strong>{selectedDevices.length} selected devices</strong>
                ?
              </>
            ) : (
              <>
                Block{' '}
                <strong>
                  {selectedDevices[0]?.email?.[0] || 'Unknown User'}
                </strong>
                's device{' '}
                <strong>{selectedDevices[0]?.model || 'Unknown Device'}</strong>
                ?
              </>
            )}
          </Typography>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: isBulk ? 'Block Devices' : 'Block Device',
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
        color: 'warning',
      }}
    />
  );
}
